import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useFetchWrapper } from '../../_helpers/fetchWrapper.js';
import { FaTimes } from 'react-icons/fa';
import styles from '../../styles/partial/PerformerEditTags.module.css';
import config from '../../config.js';
import { resolveMediaUrl } from '../../_helpers/mediaUrl.js';
import { useDrop } from 'react-dnd';
import { ItemTypes } from './navigation/PerformerNavEditTags.jsx';
import SongFilter from './SongFilter.jsx';
import { useEditTagsSearch } from '../../contexts/EditTagsSearchContext.jsx';
import { useT } from '../../contexts/LocaleContext.jsx';


const DroppableArea = React.memo(({ song, onDropTag, onDeleteTag, selectedSongs, isOverGlobal, setIsOverGlobal, isSelected }) => {
  // A chip removed from a selected song is removed from every visible
  // selected song (ENG-507), so its label says how many.
  const removalCount = isSelected ? selectedSongs.length : 1;
  const t = useT();
  const [{ isOver }, drop] = useDrop(() => ({
    accept: ItemTypes.TAG,
    drop: (item) => {
      const songId = song.id;
      onDropTag(songId, item.tag);
      setIsOverGlobal(false);
    },
    collect: (monitor) => ({
      isOver: !!monitor.isOver(),
    }),
    hover: () => {
      const songId = song.id || song.ID;
      if (selectedSongs.includes(songId)) {
        setIsOverGlobal(true);
      }
    },
    end: () => {
      setIsOverGlobal(false);
    },
  }), [selectedSongs, song.id, song.ID]);

  useEffect(() => {
    return () => {
      if (isOver) {
        setIsOverGlobal(false);
      }
    };
  }, [isOver, setIsOverGlobal]);

  const songId = song.id || song.ID;
  const isDropActive = isOver || (isOverGlobal && isSelected);
  const songTags = song.tags || [];

  const tagsCellClass = isDropActive
    ? `${styles.tagsCellDropOver}${isSelected ? ` ${styles.tagsCellSelected}` : ''}`
    : isSelected
      ? `${styles.tagsCell} ${styles.tagsCellSelected}`
      : styles.tagsCell;

  return (
    <div
      ref={drop}
      role="cell"
      className={tagsCellClass}
      data-testid={`tag-drop-${songId}`}
      onMouseLeave={() => {
        if (isOver) {
          setIsOverGlobal(false);
        }
      }}
    >
      {songTags.map((tag) => {
        const tagId = tag.ID || tag.id;
        const tagWord = tag.Word || tag.word;
        const rawTagBgColor = tag.BackgroundColor || tag.background_color;
        const rawTagTextColor = tag.TextColor || tag.text_color;
        const tagImageUrl = tag.ImageURL || tag.image_url;
        const usesLegacyDefaultColors = ['#fff', '#ffffff', 'white', 'rgb(255, 255, 255)'].includes(
          String(rawTagBgColor).trim().toLowerCase()
        );
        const hasCustomColors = rawTagBgColor && !usesLegacyDefaultColors;

        return (
          <span
            key={tagId}
            className={hasCustomColors ? styles.tagChipCustom : styles.tagChip}
            style={hasCustomColors ? { backgroundColor: rawTagBgColor, color: rawTagTextColor } : undefined}
          >
            {tagImageUrl && (
              <img
                src={resolveMediaUrl(tagImageUrl)}
                alt=""
                className={styles.tagChipImage}
              />
            )}
            {tagWord}
            <button
              className={styles.tagDeleteBtn}
              onClick={() => onDeleteTag(songId, tagId)}
              aria-label={removalCount > 1
                ? t('editTags.table.removeTagFromSelected', { tag: tagWord, count: removalCount })
                : t('editTags.table.removeTag', { tag: tagWord })}
              title={removalCount > 1 ? t('editTags.table.removeTagFromSelected', { tag: tagWord, count: removalCount }) : undefined}
              type="button"
            >
              <FaTimes size={7} />
            </button>
          </span>
        );
      })}
    </div>
  );
});

const PerformerEditTags = ({ reloadKey, resetTagFilterRef }) => {
  const { bandid } = useParams();
  const navigate = useNavigate();
  const t = useT();
  const fetchWrapper = useFetchWrapper();
  const [songs, setSongs] = useState([]);
  const [songsFetched, setSongsFetched] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });
  const [selectedSongs, setSelectedSongs] = useState([]);
  const latestSelectedSongsRef = useRef(selectedSongs);
  const lastReloadKeyRef = useRef(null);
  const [isOverGlobal, setIsOverGlobal] = useState(false);
  const [bandTags, setBandTags] = useState([]);
  const [selectedTagFilter, setSelectedTagFilter] = useState(null);
  const { searchTerm, performSearch } = useEditTagsSearch();

  useEffect(() => {
    if (resetTagFilterRef) {
      resetTagFilterRef.current = () => {
        setSelectedTagFilter(null);
      };
    }
  }, [resetTagFilterRef]);

  const fetchSongsData = useCallback(async () => {
    try {
      const [songsResponse, tagsResponse] = await Promise.all([
        fetchWrapper.get(`${config.apiBaseUrl}/bands/${bandid}/songs`),
        fetchWrapper.get(`${config.apiBaseUrl}/bands/${bandid}/tags`).catch(() => ({ success: true, tags: [] }))
      ]);

      if (songsResponse && songsResponse.success && Array.isArray(songsResponse.songs)) {
        setSongs(songsResponse.songs);
      } else if (Array.isArray(songsResponse)) {
        setSongs(songsResponse);
      } else {
        setSongs([]);
      }

      if (tagsResponse && tagsResponse.success && Array.isArray(tagsResponse.tags)) {
        setBandTags(tagsResponse.tags);
      } else if (Array.isArray(tagsResponse)) {
        setBandTags(tagsResponse || []);
      } else {
        setBandTags([]);
      }

      setSongsFetched(true);
    } catch (error) {
      console.error('Error fetching songs data:', error);
      try {
        const songsResponse = await fetchWrapper.get(`${config.apiBaseUrl}/bands/${bandid}/songs`);

        if (songsResponse && songsResponse.success && Array.isArray(songsResponse.songs)) {
          setSongs(songsResponse.songs);
        } else if (Array.isArray(songsResponse)) {
          setSongs(songsResponse);
        } else {
          setSongs([]);
        }

        setBandTags([]);
        setSongsFetched(true);
      } catch (retryError) {
        console.error('Error fetching songs data on retry:', retryError);
        setSongs([]);
        setBandTags([]);
      }
    }
  }, [bandid, fetchWrapper]);

  useEffect(() => {
    if (lastReloadKeyRef.current !== reloadKey) {
      setSongsFetched(false);
      lastReloadKeyRef.current = reloadKey;
    }

    if (songsFetched) return;

    fetchSongsData();
  }, [songsFetched, fetchSongsData, reloadKey]);

  useEffect(() => {
    latestSelectedSongsRef.current = selectedSongs;
  }, [selectedSongs]);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 480px)');
    const handleChange = (e) => {
      if (e.matches) {
        setSelectedSongs([]);
      }
    };
    mq.addEventListener('change', handleChange);
    return () => mq.removeEventListener('change', handleChange);
  }, []);

  const handleSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const sortedSongs = React.useMemo(() => {
    let sortableSongs = [...songs];

    if (selectedTagFilter) {
      sortableSongs = sortableSongs.filter(song => {
        const selectedTagId = selectedTagFilter.ID || selectedTagFilter.id;
        return song.tags && song.tags.some(tag => (tag.ID || tag.id) === selectedTagId);
      });
    }

    if (searchTerm && searchTerm.trim()) {
      const searchTermLower = searchTerm.trim().toLowerCase();
      sortableSongs = sortableSongs.filter(song => {
        if (searchTermLower.includes(' - ')) {
          const [searchTitle, searchArtist] = searchTermLower.split(' - ');
          const titleMatch = song.title && song.title.toLowerCase().includes(searchTitle.trim());
          const artistMatch = song.artist && song.artist.toLowerCase().includes(searchArtist.trim());
          if (titleMatch && artistMatch) {
            return true;
          }
        }
        const titleMatch = song.title && song.title.toLowerCase().includes(searchTermLower);
        const artistMatch = song.artist && song.artist.toLowerCase().includes(searchTermLower);
        const albumMatch = song.album && song.album.toLowerCase().includes(searchTermLower);
        return titleMatch || artistMatch || albumMatch;
      });
    }

    if (sortConfig.key !== null) {
      sortableSongs.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableSongs;
  }, [songs, sortConfig, selectedTagFilter, searchTerm]);

  // Tag drops and chip removals act on selected songs that are visible
  // (ENG-507); a song hidden by the search or the tag filter is never
  // touched. The selection is also pruned to the visible songs whenever the
  // search or the filter changes.
  const visibleIdsRef = useRef(new Set());
  visibleIdsRef.current = new Set(sortedSongs.map(song => song.id));
  useEffect(() => {
    setSelectedSongs(prevSelected => {
      const pruned = prevSelected.filter(songId => visibleIdsRef.current.has(songId));
      return pruned.length === prevSelected.length ? prevSelected : pruned;
    });
  }, [selectedTagFilter, searchTerm]);

  const visibleSelection = () => latestSelectedSongsRef.current.filter(id => visibleIdsRef.current.has(id));

  const getSortIndicator = (key) => {
    if (sortConfig.key === key) {
      return sortConfig.direction === 'ascending' ? ' ↑' : ' ↓';
    }
    return '';
  };

  const handleSelectedSong = (songId) => (event) => {
    setSelectedSongs((prevSelectedSongs) =>
      event.target.checked
        ? [...prevSelectedSongs, songId]
        : prevSelectedSongs.filter((id) => id !== songId)
    );
  };

  const handleSongTagDelete = (songId, tagId) => {
    const selection = visibleSelection();
    const songIdsToUpdate = selection.includes(songId) ? selection : [songId];

    const previousSongs = songs;

    setSongs((prevSongs) =>
      prevSongs.map((song) => {
        const id = song.id;
        if (songIdsToUpdate.includes(id)) {
          const songTags = song.tags || [];
          const updatedTags = songTags.filter((tag) => (tag.ID || tag.id) !== tagId);
          return { ...song, tags: updatedTags };
        }
        return song;
      })
    );

    const requests = songIdsToUpdate.map((id) =>
      fetchWrapper.post(`${config.apiBaseUrl}/bands/${bandid}/tags/unassign`, {
        song_id: id,
        tag_id: tagId,
      })
    );

    Promise.all(requests).catch((error) => {
      console.error('Error unassigning tag:', error);
      setSongs(previousSongs);
    });
  };

  const handleSelectAll = () => {
    const visibleSongIds = sortedSongs.map(song => song.id);
    const visibleSelectedSongs = selectedSongs.filter(id => visibleSongIds.includes(id));

    if (visibleSelectedSongs.length === visibleSongIds.length && visibleSongIds.length > 0) {
      setSelectedSongs(prevSelected => prevSelected.filter(id => !visibleSongIds.includes(id)));
    } else {
      const newSelection = [...selectedSongs.filter(id => !visibleSongIds.includes(id)), ...visibleSongIds];
      setSelectedSongs(newSelection);
    }
  };

  const handleDropTag = (songId, tag) => {
    const currentSelection = visibleSelection();
    let songIdsToUpdate;
    if (currentSelection.includes(songId)) {
      songIdsToUpdate = currentSelection;
    } else {
      songIdsToUpdate = [songId];
    }

    const previousSongs = songs;

    setSongs((prevSongs) =>
      prevSongs.map((song) => {
        if (songIdsToUpdate.includes(song.id)) {
          const tagId = tag.ID || tag.id;
          const tagExists = (song.tags || []).some((t) => (t.ID || t.id) === tagId);
          if (!tagExists) {
            return { ...song, tags: [...(song.tags || []), tag] };
          }
        }
        return song;
      })
    );

    const requests = songIdsToUpdate
      .filter((id) => {
        const song = songs.find((s) => s.id === id);
        if (!song) return false;
        const tagId = tag.ID || tag.id;
        return !(song.tags || []).some((t) => (t.ID || t.id) === tagId);
      })
      .map((id) =>
        fetchWrapper.post(`${config.apiBaseUrl}/bands/${bandid}/tags/assign`, {
          song_id: id,
          tag_id: tag.ID || tag.id,
        })
      );

    Promise.all(requests).catch((error) => {
      console.error('Error assigning tag:', error);
      setSongs(previousSongs);
    });
  };

  const visibleSongIds = sortedSongs.map(song => song.id);
  const visibleSelectedSongs = selectedSongs.filter(id => visibleSongIds.includes(id));
  const allVisibleSelected = visibleSelectedSongs.length === visibleSongIds.length && visibleSongIds.length > 0;
  const someVisibleSelected = visibleSelectedSongs.length > 0 && !allVisibleSelected;

  const masterCheckboxRef = useRef(null);
  useEffect(() => {
    if (masterCheckboxRef.current) {
      masterCheckboxRef.current.indeterminate = someVisibleSelected;
    }
  }, [someVisibleSelected]);

  const handleBack = () => {
    navigate(`/performers/${bandid}/song-list`);
  };

  return (
    <div className={styles.page}>
      <div className={styles.titleRow}>
        <button type="button" className={styles.backBtn} onClick={handleBack}>
          {t('editTags.back')}
        </button>
        <h2 className={styles.title}>{t('editTags.title')}</h2>
        <p className={styles.subtitle}>{t('editTags.subtitle')}</p>
      </div>

      <SongFilter
        bandTags={bandTags}
        selectedTagFilter={selectedTagFilter}
        onFilterChange={setSelectedTagFilter}
        searchTerm={searchTerm}
        onSearchChange={performSearch}
      />

      <div className={styles.tableContainer} role="table" aria-label={t('editTags.table.label')}>
        <div role="rowgroup">
          <div role="row" className={styles.colLabels}>
            <div role="columnheader" className={styles.colLabelCheckbox}>
              <input
                ref={masterCheckboxRef}
                type="checkbox"
                className={styles.masterCheckbox}
                aria-label={allVisibleSelected ? t('editTags.table.deselectAll') : t('editTags.table.selectAll')}
                onChange={handleSelectAll}
                checked={allVisibleSelected}
              />
            </div>
            <div
              role="columnheader"
              className={`${styles.colLabelText} ${styles.colLabelTitle}`}
              onClick={() => handleSort('title')}
              aria-sort={sortConfig.key === 'title' ? sortConfig.direction : 'none'}
            >
              {t('editTags.table.title')}{getSortIndicator('title')}
            </div>
            <div
              role="columnheader"
              className={`${styles.colLabelText} ${styles.colLabelArtist}`}
              onClick={() => handleSort('artist')}
              aria-sort={sortConfig.key === 'artist' ? sortConfig.direction : 'none'}
            >
              {t('editTags.table.artist')}{getSortIndicator('artist')}
            </div>
            <div role="columnheader" className={`${styles.colLabelText} ${styles.colLabelTags}`}>
              {t('editTags.table.tags')}
            </div>
          </div>
        </div>

        {sortedSongs.length === 0 ? (
          <div role="rowgroup">
            <div role="row">
              <div role="cell" className={styles.emptyState}>{t('editTags.table.empty')}</div>
            </div>
          </div>
        ) : (
          <div role="rowgroup" className={styles.songRowGroup}>
          {sortedSongs.map(song => {
            const songId = song.id;
            const isSelected = selectedSongs.includes(songId);
            return (
              <div
                key={songId}
                role="row"
                className={`${styles.songRow} ${isSelected ? styles.songRowSelected : styles.songRowNormal}`}
              >
                <div className={styles.checkboxCell} role="cell">
                  <input
                    type="checkbox"
                    className={styles.rowCheckbox}
                    id={`song-${songId}`}
                    aria-label={t('editTags.table.selectSong', { title: song.title })}
                    onChange={handleSelectedSong(songId)}
                    checked={isSelected}
                  />
                </div>
                <div className={styles.titleCell} role="cell">{song.title}</div>
                <div className={styles.artistCell} role="cell">{song.artist}</div>
                <DroppableArea
                  song={song}
                  onDropTag={handleDropTag}
                  onDeleteTag={handleSongTagDelete}
                  selectedSongs={visibleSelectedSongs}
                  isSelected={isSelected}
                  isOverGlobal={isOverGlobal}
                  setIsOverGlobal={setIsOverGlobal}
                />
              </div>
            );
          })}
          </div>
        )}
      </div>
    </div>
  );
};

export default PerformerEditTags;
